/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { RootState } from "reducers";
import { Globals, Socket, SocketEvents } from 'utils';
import { ChatService } from 'services';
import SentIcon from 'assets/icons/sent.png';
import SearchIcon from 'assets/icons/search.png';
import MessageIcon from 'assets/img/message.png';
import { FallbackImage } from "components";
import { WhiteImage } from "assets/img";
import { useLocation } from "react-router-dom";

type ChatMessage = any;
type ChatForm = any;
type ConversationForm = any;
type Chat = any;

const getDate = (date?: string) => {
  if (!date) return '';

  const selected = moment(date).format('YYYY-MM-DD');
  const today = moment().format('YYYY-MM-DD');

  if (selected === today)
    return moment(date).format('h:mm a');

  const yesterday = moment().subtract(1,'day').format('YYYY-MM-DD');
  if (selected === yesterday)
    return 'Ayer';

  return moment(date).format('DD/MM/YYYY');
}

const getMessageDate = (date: string) => {
  const selected = moment(date).format('YYYY-MM-DD');
  const today = moment().format('YYYY-MM-DD');

  if (selected === today)
    return moment(date).format('h:mm a');

  return moment(date).format('DD/MM/YYYY h:mm a');
}

const ChatConversation = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const { chatForm, chats, conversation, conversationForm, disabledConversationInput, onScrollChats, onSearchChats, onTextChange, scrollViewRef, selectChat, sendMessage } = useForm();

  useEffect(() => {
    dispatch({ type: 'SET_HEADER', payload: { title: 'Chat' } });
  }, []);

  return (
    <main id="chat">
      <section className="conversations">
        <div className="search">
          <div className="input-search">
            <img src={SearchIcon} alt="Search" />
            <input
              placeholder="Buscar"
              value={chatForm.search}
              onChange={onSearchChats}
            />
          </div>
        </div>
        <ul className="chat-list" onScroll={onScrollChats}>
          {chats.map((item, index) => {
            const isSelected = conversationForm.chat_id === item.id;
            const userNames = item.user_id === user?.id ? user?.doctor?.name : item?.user?.name;

            return (
              <li
                key={index}
                className={Globals.classNames('conv-item', isSelected ? 'conv-item-active' : '')}
                onClick={() => selectChat(item, isSelected ? undefined : item.id)}
              >
                <FallbackImage
                  src={item.user?.photo}
                  placeholder={WhiteImage}
                  className="conv-avatar"
                  placeholderClassName="conv-avatar"
                  alt={userNames}
                />
                <div className="conv-content">
                  <div className="conv-top">
                    <p className="conv-contact" title={userNames}>{ userNames }</p>
                    {!!item.counter && (
                      <span className="conv-counter">{ item.counter }</span>
                    )}
                  </div>
                  <div className="conv-details">
                    {!!item.last_message?.message ? (
                      <div className="conv-last-msg" title={item.last_message?.message}>
                        { item.last_message?.message }
                      </div>
                    ) : (
                      <div className="conv-last-msg" title="No hay mensajes">
                        <i>No hay mensajes</i>
                      </div>
                    )}
                    <div className="conv-date">
                      { getDate(item.last_message?.created_at) }
                    </div>
                  </div>
                </div>
              </li>
            )
          })}

          {(!chatForm.isLoading && !chats.length) && (
            <p className="empty-conv">No hay mensajes</p>
          )}

          {chatForm.isLoading && (
            <div style={{ margin: '1rem 0', display: 'flex', justifyContent: 'center' }}>
              <div className="loader" />
            </div>
          )}
        </ul>
      </section>

      <section className="conversation">
        {!!conversationForm.chat_id ? (
          <>
            <ul className="messages" ref={scrollViewRef}>
              {!!conversationForm.chat_id && conversation.map((item, index) => {
                const isOut = item.user_id === user.id;
                const isIn = !!item.user_id && item.user_id !== user.id;

                return (
                  <li key={index} className={Globals.classNames('msg-item', isIn ? 'in' : '', isOut ? 'out' : '')}>
                    <p className="msg-text">{ item.message }</p>
                    <p className="msg-date">{ getMessageDate(item.created_at) }</p>
                  </li>
                )
              })}

              {conversationForm.isLoading && (
                <div style={{ margin: 'auto' }}>
                  <div className="loader" />
                </div>
              )}
            </ul>
            {!disabledConversationInput && (
              <footer>
                <form>
                  <textarea
                    rows={1}
                    placeholder="Escribir mensaje"
                    value={conversationForm.input}
                    onChange={onTextChange}
                  />
                  <button type="button" disabled={!conversationForm.input} onClick={() => sendMessage()}>
                    <img src={SentIcon} alt="Send message" />
                  </button>
                </form>
              </footer>
            )}
          </>
        ) : (
          <div className="unselected-chat">
            <img src={MessageIcon} alt="Chats" />
            <p>Seleccione una conversación</p>
          </div>
        )}
      </section>
    </main>
  )
}

const useForm = () => {
  const user = useSelector((state: RootState) => state.user);

  const initialChatForm: ChatForm = {
    _user_id: user.id,
    page: 1,
    pages: 1,
    search: '',
    isLoading: true,
  };

  const initialConversationForm: ConversationForm = {
    chat_id: undefined,
    last_message_id: undefined,
    is_last_message: false,
    input: '',
    isLoading: false,
  };

  const location = useLocation();
  const [chatForm, setChatForm] = useState(initialChatForm);
  const [conversationForm, setConversationForm] = useState(initialConversationForm);
  const [chats, setChats] = useState<Chat[]>([]);
  const [conversation, setConversation] = useState<ChatMessage[]>([]);
  const [disabledConversationInput, setDisabledConversationInput] = useState(false);

  const debounceTime = 500;
  const debounce = useRef<NodeJS.Timeout>();
  const scrollViewRef = useRef<HTMLUListElement>(null);

  const getChats = async (form: ChatForm) => {
    if (!form.isLoading) return;
    try {
      const res: any = await ChatService.admin.get(form);

      form.page > 1
        ? setChats([ ...chats, ...res.data ])
        : setChats(res.data);
      setChatForm((s: any) => ({ ...s, pages: res.last_page, isLoading: false }));

    } catch (error) {
      console.log(error);
    }
  }

  const getConversation = async (form: ConversationForm) => {
    if (!form.isLoading) return;
    try {
      const res: any = await ChatService.admin.view(form);
      const _conversation = form.last_message_id
        ? [ ...conversation, ...res?.messages ]
        : res?.messages;

      const noMoreMessage = !res?.messages?.length || res?.messages?.length < 20;

      setConversation(_conversation);
      setConversationForm((s: any) => ({
        ...s,
        ...form,
        is_last_message: noMoreMessage,
        last_message_id: noMoreMessage
          ? form.last_message_id
          : _conversation[_conversation.length - 1].id,
        isLoading: false,
      }));

      goToRecentMessage();

    } catch (error) {
      console.log(error);
    }
  }

  const setViewedChat = async (chat_id: number) => {
    try {
      await ChatService.admin.viewed({ _user_id: user?.id, chat_id, withoutLoading: true });
      const _chats = Globals.clone(chats);
      const idx = chats.findIndex(x => x.id === chat_id);
      if (idx < 0) return;

      _chats[idx].counter = 0;
      setChats(_chats);

    } catch (error) {
      console.log(error);
    }
  }

  const onScrollChats = (event: React.UIEvent<HTMLUListElement>) => {
    const elementHeight = event.currentTarget.clientHeight;
    const totalHeight = event.currentTarget.scrollHeight;
    const currentPosition = event.currentTarget.scrollTop;
    const isInTop = totalHeight === (Math.abs(currentPosition) + elementHeight);
    const isLastPage = chatForm.page === chatForm.pages;

    if (isInTop && !isLastPage && !chatForm.isLoading) {
      const state: ChatForm = {
        ...chatForm,
        page: chatForm.page + 1,
        isLoading: true,
      };

      setChatForm(state);
      getChats(state);
    }
  }

  const selectChat = (chat: Chat, chatId?: number) => {
    const state: ConversationForm = {
      ...conversationForm,
      last_message_id: undefined,
      chat_id: chatId,
      isLoading: !!chatId,
    };
    setConversationForm(state);
    setConversation([]);
    getConversation(state);

    // Si tiene mensajes sin leer se marca como leido
    if (chatId && chat.counter > 0) setViewedChat(chatId);

    // Si alguno de los usuarios está desactivado se inhabilita el input
    if (chatId) {
      const isSomeInactive = chat.users?.some((x: any) => x?.user?.status === 0);
      setDisabledConversationInput(!!isSomeInactive);
    }
  }

  const goToRecentMessage = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({ top: scrollViewRef.current?.scrollHeight });
    }, 100);
  }

  const onSearchChats = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (debounce.current) clearTimeout(debounce.current);
    const state: ChatForm = {
      page: 1,
      pages: 1,
      isLoading: true,
      _user_id: user.id,
      search: event.currentTarget.value,
    };

    setChatForm(state);
    debounce.current = setTimeout(() => {
      setChatForm({ ...state, isLoading: true });
      getChats(state);
    }, debounceTime);
  }

  const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setConversationForm({ ...conversationForm, input: event.currentTarget.value });
  }

  const sendMessage = async () => {
    if (!conversationForm.input) return;

		await ChatService.admin.send({
      chat_id: conversationForm.chat_id,
      message: conversationForm.input,
      _user_id: user.id,
      withoutLoading: true,
		});

    setConversationForm((s: any) => ({ ...s, input: '' }));
  }

  const updateContent = (message: ChatMessage) => {
    // Verifica que el mensaje sea para el usuario actual
    if (![message?.chat?.user_id, message?.chat?.doctor_id].includes(user.id)) return;

    // Si el chat no está en el listado recargar el listado
    const isChatInList = chats.some(x => x.id === message.chat_id);
    if (!isChatInList) {
      getChats({ ...chatForm, page: 1, pages: 1, isLoading: true });
    }

    // Si el chat está en el listado y no está seleccionado, recargar
    const isSelectedChat = conversationForm.chat_id === message.chat_id;
    if (isChatInList && !isSelectedChat) {
      getChats({ ...chatForm, page: 1, pages: 1, isLoading: true });
    }

    // Si el chat está seleccionado inserta el mensaje entrante al listado
    if (isSelectedChat) {
      setConversation(s => s.concat([message]));

      if (conversationForm.chat_id && message.user_id !== user.id)
        setViewedChat(conversationForm.chat_id);
    }

    goToRecentMessage();
  }

  useEffect(() => {
    Socket.on(SocketEvents.CHATS.MESSAGE, (message: ChatMessage) => updateContent(message));

    // Ingresa automáticamente al chat si existe
    if (location.search && !conversationForm.chat_id) {
      const value = new URLSearchParams(location.search).get('chat_id');
      if (value) {
        const decoded = atob(value);
        const chatId = Number(decoded.split('&')[0]);
        const chat = chats.find(x => x.id === Number(chatId));
        if (chat) selectChat(chat, chat.id);
      }
    }

    return () => {
      Socket.off(SocketEvents.CHATS.MESSAGE);
    }
  }, [chats, conversationForm]);

  useEffect(() => {
    getChats(initialChatForm);
  }, []);

  return {
    chatForm,
    chats,
    conversation,
    conversationForm,
    disabledConversationInput,
    onScrollChats,
    onSearchChats,
    onTextChange,
    scrollViewRef,
    selectChat,
    sendMessage,
  }
}

export default ChatConversation;